import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import type { DashboardStats } from '../types';

interface HeroStatsCardProps {
  stats: DashboardStats;
}

export function HeroStatsCard({ stats }: HeroStatsCardProps) {
  const { t, i18n } = useTranslation();
  const sym = useCurrencySymbol();
  const isHe = i18n.language === 'he';
  const trendPositive = stats.trend >= 0;

  return (
    <div className="rounded-3xl border border-white/20 p-6 text-white shadow-sm" style={{ background: 'linear-gradient(135deg, var(--color-primary-600), var(--color-primary-700))' }}>
        {/* Main earnings */}
        <div className="mb-6">
          <p className="text-sm font-medium text-primary-100">{t('dashboard.totalEarnings')}</p>
          <div className="mt-1 flex items-baseline gap-3">
            <span className="text-4xl font-bold tracking-tight">
              {Math.round(stats.totalEarnings).toLocaleString()} {sym}
            </span>
            {stats.trend !== 0 && (
              <span
                className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${
                  trendPositive ? 'border-accent-500/30 bg-accent-500/20 text-accent-200' : 'border-red-400/30 bg-red-400/20 text-red-200'
                }`}
              >
                {trendPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {Math.abs(stats.trend)}%
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-primary-200">{t('dashboard.vsLastMonth')}</p>
        </div>

        {/* Dynamic sub-stats grid */}
        <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${stats.byReportType.length}, 1fr)` }}>
          {stats.byReportType.map((rt) => (
            <div key={rt.code} className="rounded-xl border border-white/20 bg-white/10 px-3 py-2 backdrop-blur-sm">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-primary-200">{isHe ? rt.name : rt.nameEn}</span>
              </div>
              <p className="mt-1 text-xl font-bold">{rt.count}</p>
              {rt.earnings > 0 && (
                <p className="text-[10px] text-primary-200/70">{Math.round(rt.earnings).toLocaleString()} {sym}</p>
              )}
            </div>
          ))}
        </div>
    </div>
  );
}
